﻿import { ReactElement } from "react";
import AsyncSelect, { AsyncProps } from "react-select/async";
import { ControlProps, CSSObjectWithLabel, GroupBase, OnChangeValue } from "react-select";
import { Flex, Spacer, Text } from "@chakra-ui/react";
import { Controller, UseControllerProps } from "react-hook-form";

export const menuStyles = (provided: CSSObjectWithLabel) => {
    return {
        ...provided,
        width: "fit-content",
    };
};

export const controlStyles = <V extends Value>(provided: CSSObjectWithLabel, state: ControlProps<Option<V>>) => {
    return {
        ...provided,
        "&:hover": { borderColor: "var(--chakra-colors-blue-500" },
        borderColor: state.isFocused ? "0 0 0 1px var(--chakra-colors-blue-500)" : "inherit",
        boxShadow: state.isFocused ? "0 0 0 1px var(--chakra-colors-blue-500)" : "none",
        borderRadius: "var(--chakra-radii-md)",
    };
};

export const multiValueRemoveStyles = (provided: CSSObjectWithLabel) => {
    return {
        ...provided,
        "&:hover": { backgroundColor: "var(--chakra-colors-blackAlpha-100)", color: "unset" },
    };
};

export const defaultStyles = {
    menu: menuStyles,
    control: controlStyles,
    multiValueRemove: multiValueRemoveStyles,
};

export type Value = string | number;

export interface Option<V extends Value> {
    value: V;
    label: string;
    before?: ReactElement;
    after?: ReactElement;
    isDisabled?: boolean;
}

export const formatOptionLabel = <V extends Value>({ label, before, after }: Option<V>) => (
    <Flex gap={".5rem"} alignItems={"center"}>
        {before}
        <Text whiteSpace={"nowrap"} color={"unset"}>
            {label}
        </Text>
        {!!after && <Spacer />}
        {after}
    </Flex>
);

type PropOverrides<V extends Value> = Pick<AsyncProps<Option<V>, boolean, GroupBase<Option<V>>>, "onChange">;
type RemainingProps<V extends Value> = Omit<AsyncProps<Option<V>, boolean, GroupBase<Option<V>>>, "onChange">;

type AutoCompleteMultiPropOverrides<V extends Value> = {
    onChange?: (newValue: Option<V>[]) => void;
};
type AutoCompleteMultiProps<V extends Value> = AutoCompleteMultiPropOverrides<V> & RemainingProps<V>;

export const AutoCompleteMulti = <V extends Value>({ onChange, styles, ...props }: AutoCompleteMultiProps<V>) => {
    const overrides: PropOverrides<V> = {};

    if (onChange) {
        overrides.onChange = (newValue: OnChangeValue<Option<V>, boolean>) => {
            onChange(newValue as Option<V>[]);
        };
    }

    return (
        <AsyncSelect
            isClearable
            isMulti
            isSearchable
            styles={{
                ...defaultStyles,
                ...styles,
            }}
            formatOptionLabel={formatOptionLabel}
            {...props}
            {...overrides}
        />
    );
};

export type AutoCompleteMultiControlledProps<V extends Value> = AutoCompleteMultiProps<V> & UseControllerProps;

/**
 * This is a convenience component that makes it easy to use the AutoCompleteSingle component with react-hook-form.
 * @param control
 * @param name
 * @param props
 * @param rules
 * @param defaultValue
 * @param disabled
 * @param shouldUnregister
 * @constructor
 */
export const AutoCompleteMultiControlled = <V extends Value>({
    control,
    name,
    rules,
    defaultValue,
    disabled,
    shouldUnregister,
    ...props
}: AutoCompleteMultiControlledProps<V>) => {
    return (
        <Controller
            control={control}
            name={name}
            rules={rules}
            defaultValue={defaultValue}
            disabled={disabled}
            shouldUnregister={shouldUnregister}
            render={({ field: { onChange, value } }) => {
                return (
                    <AutoCompleteMulti
                        onChange={newValue => {
                            onChange(newValue);
                        }}
                        value={value}
                        {...props}
                    />
                );
            }}
        />
    );
};

type AutoCompleteSinglePropOverrides<V extends Value> = {
    onChange?: (newValue: Option<V>) => void;
};

type SinglePropOverrides<V extends Value> = Pick<AsyncProps<Option<V>, false, GroupBase<Option<V>>>, "onChange">;
type SingleRemainingProps<V extends Value> = Omit<AsyncProps<Option<V>, false, GroupBase<Option<V>>>, "onChange">;

type AutoCompleteSingleProps<V extends Value> = AutoCompleteSinglePropOverrides<V> & SingleRemainingProps<V>;

export const AutoCompleteSingle = <V extends Value>({ onChange, styles, ...props }: AutoCompleteSingleProps<V>) => {
    const overrides: SinglePropOverrides<V> = {};

    if (onChange) {
        overrides.onChange = (newValue: OnChangeValue<Option<V>, boolean>) => {
            onChange(newValue as Option<V>);
        };
    }

    return (
        <AsyncSelect
            isClearable
            isMulti={false}
            isSearchable
            styles={{
                control: controlStyles,
                ...styles,
            }}
            formatOptionLabel={formatOptionLabel}
            {...props}
            {...overrides}
        />
    );
};

export type AutoCompleteSingleControlledProps<V extends Value> = AutoCompleteSingleProps<V> & UseControllerProps;

/**
 * This is a convenience component that makes it easy to use the AutoCompleteSingle component with react-hook-form.
 * @param control
 * @param name
 * @param props
 * @param rules
 * @param defaultValue
 * @param disabled
 * @param shouldUnregister
 * @constructor
 */
export const AutoCompleteSingleControlled = <V extends Value>({
    control,
    name,
    rules,
    defaultValue,
    disabled,
    shouldUnregister,
    ...props
}: AutoCompleteSingleControlledProps<V>) => {
    return (
        <Controller
            control={control}
            name={name}
            rules={rules}
            defaultValue={defaultValue}
            disabled={disabled}
            shouldUnregister={shouldUnregister}
            render={({ field: { onChange, value } }) => {
                return (
                    <AutoCompleteSingle
                        onChange={newValue => {
                            onChange(newValue);
                        }}
                        value={value}
                        {...props}
                    />
                );
            }}
        />
    );
};
